import { Trans } from '@lingui/macro'
import { Box, Divider, List, ListItem, ListItemText, Stack, Typography } from '@mui/material'
import CheckRoundedIcon from '@mui/icons-material/CheckRounded'
import ClearRoundedIcon from '@mui/icons-material/ClearRounded'

import ChipButton from '@/components/ui/buttons/ChipButton'
import { ExpertiseMeeting } from '@/core/models/expertise-meeting.model'
import { useGetExpertiseMeetingQuery } from '@/core/services/expertise-meeting'

import { useReplyRequestAccess } from '../model/useReplyRequestAccess'

export function Attendees({ expertiseMeetingId }: { expertiseMeetingId: string }) {
  const { data: expertiseMeeting } = useGetExpertiseMeetingQuery(expertiseMeetingId)
  return (
    <Stack divider={<Divider />} direction={expertiseMeeting?.pendingAttendees.length ? 'column' : 'column-reverse'} spacing={2}>
      <div>
        <Typography component="h2" variant="h2">
          <Trans>Pending attendees</Trans>
        </Typography>
        <PendingAttendeesList expertiseMeetingId={expertiseMeetingId} />
      </div>
      <div>
        <Typography component="h2" variant="h2">
          <Trans>Attendees</Trans>
        </Typography>
        <AttendeesList expertiseMeetingId={expertiseMeetingId} />
      </div>
    </Stack>
  )
}

function Attendee({ attendee }: { attendee: ExpertiseMeeting['attendees'][number] }) {
  return (
    <ListItem disableGutters>
      <ListItemText primary={`${attendee.userInfo.name} ${attendee.userInfo.surname}`} secondary={attendee.email} sx={{ m: 0 }} />
    </ListItem>
  )
}

function AttendeesList({ expertiseMeetingId }: { expertiseMeetingId: string }) {
  const { data: expertiseMeeting } = useGetExpertiseMeetingQuery(expertiseMeetingId)

  if (!expertiseMeeting?.attendees.length) {
    return (
      <Box pt={1}>
        <Trans>No attendees</Trans>
      </Box>
    )
  }

  return (
    <List disablePadding>
      {expertiseMeeting.attendees.map((attendee) => (
        <Attendee key={attendee._id} attendee={attendee} />
      ))}
    </List>
  )
}

function PendingAttendee({ attendee, expertiseMeetingId }: { attendee: ExpertiseMeeting['pendingAttendees'][number]; expertiseMeetingId: string }) {
  const { acceptRequestAccess, declineRequestAccess, isAccepting, isDeclining } = useReplyRequestAccess(expertiseMeetingId)

  return (
    <ListItem
      key={attendee._id}
      disableGutters
      secondaryAction={
        <Stack direction="row" spacing={1}>
          <ChipButton
            color="success"
            loading={isAccepting}
            onClick={() => {
              acceptRequestAccess(attendee._id)
            }}
          >
            <CheckRoundedIcon />
          </ChipButton>
          <ChipButton
            color="error"
            loading={isDeclining}
            onClick={() => {
              declineRequestAccess(attendee._id)
            }}
          >
            <ClearRoundedIcon />
          </ChipButton>
        </Stack>
      }
    >
      <ListItemText primary={`${attendee.userInfo.name} ${attendee.userInfo.surname}`} secondary={attendee.email} sx={{ m: 0 }} />
    </ListItem>
  )
}

function PendingAttendeesList({ expertiseMeetingId }: { expertiseMeetingId: string }) {
  const { data: expertiseMeeting } = useGetExpertiseMeetingQuery(expertiseMeetingId)
  if (!expertiseMeeting?.pendingAttendees.length) {
    return (
      <Box pt={1}>
        <Trans>No pending attendees</Trans>
      </Box>
    )
  }

  return (
    <List disablePadding>
      {expertiseMeeting.pendingAttendees.map((attendee) => (
        <PendingAttendee key={attendee._id} attendee={attendee} expertiseMeetingId={expertiseMeetingId} />
      ))}
    </List>
  )
}
